/**
 * Cart line variant selector: lets the customer change the variant (e.g. size)
 * of a line item without removing it. Adds the new variant and removes the old line.
 */

import { h, render } from 'preact'
import { cart, Cart } from '@/components/helpers/cart'
import { ShopifySectionHelper } from '@/components/helpers/section-render'
import { globalState } from '@/components/helpers/utils'
import { globalEmitter, emitterProps } from '@/components/helpers/emitter-event'
import Icon from 'snippets/icon/icon'

export class CartLineVariantSelectorElement extends HTMLElement {
  private select: HTMLSelectElement | null = null
  private boundChange = this.handleChange.bind(this)

  connectedCallback() {
    this.select = this.querySelector('select')
    this.select?.addEventListener('change', this.boundChange)
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.boundChange)
  }

  private get lineKey(): string {
    return this.getAttribute('data-line-key') || ''
  }

  private get quantity(): number {
    return parseInt(this.getAttribute('data-quantity') || '1', 10) || 1
  }

  private get currentVariantId(): string {
    return this.getAttribute('data-current-variant-id') || ''
  }

  private async handleChange() {
    if (!this.select) return
    const newVariantId = this.select.value
    if (!newVariantId || newVariantId === this.currentVariantId) return

    const key = this.lineKey
    if (!key) return

    const previousQuantity = this.quantity
    this.select.disabled = true
    try {
      Cart.skipNextCartPageRefresh = true
      await cart.addLineItems([{ id: parseInt(newVariantId, 10), quantity: previousQuantity }], { silent: true })
      const changeRes = await cart.changeLineItem({ id: key, quantity: 0 }, { silent: true })
      if (changeRes?.error) {
        globalEmitter.emit(emitterProps.notice.global, { type: 'error', content: changeRes.error })
        this.select.value = this.currentVariantId
        return
      }
      await ShopifySectionHelper.fetchAndUpdatePage({
        url: globalState?.routes?.cart_url || '/cart',
        fetchSelectors: ['#cart-items-inner', '#cart-summary'],
        targetSelectors: ['#cart-items-inner', '#cart-summary']
      })
      window.lazyLoadInstance?.update()
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
      })

      const lineItem = document.querySelector<HTMLElement>(`[data-cart-line-item][data-variant-id="${newVariantId}"]`)
      const qtyInput = lineItem?.querySelector<HTMLInputElement>('input[data-quantity-input]')
      const newQuantity = parseInt(qtyInput?.value ?? '', 10) || 0
      if (newQuantity < previousQuantity) {
        this.showQuantityAdjustedError(lineItem!)
      }
    } catch (err) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: err instanceof Error ? err.message : 'Could not update variant'
      })
      this.select.value = this.currentVariantId
    } finally {
      this.select.disabled = false
    }
  }

  private showQuantityAdjustedError(lineItem: HTMLElement) {
    const existing = lineItem.querySelector('[data-cart-line-quantity-adjusted]')
    if (existing) return

    const message = document.createElement('div')
    message.setAttribute('data-cart-line-quantity-adjusted', '')
    message.className =
      'flex items-center gap-x-0.5 w-full px-1 py-0.75 lg:py-0.875 rounded-8 border border-borders-error bg-page-error paragraph-s'

    const iconWrap = document.createElement('span')
    iconWrap.className = 'shrink-0 flex size-[15px] text-type-error children:size-full'
    iconWrap.setAttribute('aria-hidden', 'true')

    const textSpan = document.createElement('span')
    textSpan.innerHTML = '<span class="font-medium">Not enough stock of style.</span> Quantity adjusted.'

    render(h(Icon, { id: 'error-small', className: 'size-full' }), iconWrap)
    message.appendChild(iconWrap)
    message.appendChild(textSpan)
    lineItem.appendChild(message)
  }
}
